import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@components/atoms/Button';
import { useTranslation } from '@hooks/useTranslation';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { setLocale } from '@redux/reducers/locale.reducer';
import { useUpdateCurrentUser } from '@hooks/api';
import { UserLocale } from '@models/user.model';
import locales from '@locales';

interface LanguageSwitcherProps {
  userId?: string;
}

/**
 * Sprint 27, story-139 (OYScba1p). Language section on the web Profile page —
 * docs/ux/story-139-design.md, Wireframe A / Flow A.
 *
 * Staged selection, explicit save (matches Profile Information / Change Password on this same
 * page). Save dispatches the new locale into Redux immediately (optimistic — the concrete fix for
 * "applies without requiring a re-login", since `setLocale` is a plain action reachable from any
 * code path, not just the auth ones), then persists via `useUpdateCurrentUser`. On failure, the
 * locale (and the staged selection) revert, and the error toast is shown in the locale that's
 * active again after the revert.
 */
export const LanguageSwitcher: FC<LanguageSwitcherProps> = ({ userId }) => {
  const dispatch = useAppDispatch();
  const activeLocale = useAppSelector((state: RootState) => state.locale.activeLocale);
  const t = useTranslation('settings');
  const [pendingValue, setPendingValue] = useState<string>(activeLocale);
  // Suppress the hook's own (hardcoded-English) toasts — this component already shows its own
  // localized success/error toast below, and showing both produced a duplicate, partly
  // unlocalized toast on the translations feature itself (sprint 27 review finding).
  const { mutateAsync: updateCurrentUser, isPending: saving } = useUpdateCurrentUser({
    suppressSuccessToast: true,
    suppressErrorToast: true,
  });

  const options: { value: UserLocale; label: string }[] = [
    { value: UserLocale.EN, label: t.english },
    { value: UserLocale.ES, label: t.spanish },
  ];

  const handleSave = async () => {
    const previousLocale = activeLocale;
    if (pendingValue === previousLocale) return;

    // Optimistic: the UI (including this section) updates to the new language immediately,
    // before the persistence call resolves.
    dispatch(setLocale(pendingValue));

    try {
      await updateCurrentUser({ userId, locale: pendingValue as UserLocale });
      const newCatalog = (locales as { [key: string]: any })[pendingValue];
      toast.success(newCatalog?.settings?.languageUpdated || 'Language updated');
    } catch (error) {
      // Revert both the visible language and the staged selection, and show the error in the
      // locale that's active again — never leaving the user reading an error in a language they
      // didn't choose.
      dispatch(setLocale(previousLocale));
      setPendingValue(previousLocale);
      const previousCatalog = (locales as { [key: string]: any })[previousLocale];
      toast.error(
        previousCatalog?.settings?.languageUpdateError || 'Could not update language. Please try again.',
      );
    }
  };

  return (
    <div className="mt-10 max-w-xl">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t.language}</h2>
      <div className="flex gap-3">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={saving}
            onClick={() => setPendingValue(option.value)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${
              pendingValue === option.value
                ? 'border-indigo-900 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-900 dark:text-indigo-300'
                : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
            }`}>
            <span
              aria-hidden
              className={`inline-block w-3 h-3 rounded-full border ${
                pendingValue === option.value
                  ? 'border-indigo-900 dark:border-indigo-500 bg-indigo-900 dark:bg-indigo-500'
                  : 'border-gray-400'
              }`}
            />
            {option.label}
          </button>
        ))}
      </div>
      <div className="mt-4">
        <Button
          type="button"
          variant="primary"
          loading={saving}
          disabled={saving || pendingValue === activeLocale}
          onClick={handleSave}>
          {t.saveLanguage}
        </Button>
      </div>
    </div>
  );
};

export default LanguageSwitcher;
